<template>
    <div
        class="dashboard-top-card col-span-12 lg:col-span-8 overflow-hidden shadow rounded-lg"
        :class="{ 'bg-white': !loading, 'bg-gray-200': loading }"
    >
        <TopCardSkeleton v-if="loading" />
        <div
            v-if="!loading"
            class="bg-background-white rounded-lg p-6 h-auto relative overflow-hidden"
            style="
                box-shadow: var(
                    --shadow-tailwind-base-box-shadow,
                    0px 1px 2px 0px rgba(0, 0, 0, 0.06),
                    0px 1px 3px 0px rgba(0, 0, 0, 0.1)
                );
            "
        >
            <div class="flex flex-row items-center justify-between w-full">
                <div
                    class="flex flex-row gap-2 items-center justify-start shrink-0 relative"
                >
                    <icon icon="credit-card" />
                    <div
                        class="text-slate-800 text-lg text-left relative flex items-center justify-start font-medium"
                    >
                        Next card
                    </div>
                </div>
                <a
                    href="/mynextcard/"
                    class="flex items-center text-sm font-medium rounded text-slate-800 hover:text-slate-700 active:text-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-300 focus:ring-offset-0"
                    style="
                        --tw-ring-color: rgb(203 213 225);
                        outline: none !important;
                    "
                    tabindex="0"
                >
                    View all
                    <icon icon="arrow" />
                </a>
            </div>

            <div
                class="flex flex-col items-start justify-start w-full sm:flex-row sm:items-start"
            >
                <img
                    :src="`${cdnUrl}/${card.card_image?.path}/_small/${card.card_image?.file_name}`"
                    :alt="card.title"
                    class="rounded-xl w-full sm:w-[250px] lg:w-[356px] h-auto z-20 mt-12 filter drop-shadow-lg"
                />

                <div
                    class="flex flex-col gap-0 items-start justify-start w-full sm:w-[464px] ml-0 sm:ml-4 md:ml-4 lg:ml-8 mt-10 z-20"
                >
                    <div
                        class="flex flex-col gap-0 items-start justify-start self-stretch shrink-0 relative"
                    >
                        <div
                            class="flex flex-col gap-4 items-start justify-start self-stretch shrink-0 relative"
                        >
                            <div
                                class="flex flex-col gap-2 items-start justify-start shrink-0 relative"
                            >
                                <div
                                    class="text-slate-800 text-lg sm:text-xl md:text-2xl text-left font-semibold relative w-full md:w-[400px] lg:w-[482px] flex items-center justify-start"
                                >
                                    {{ card.title }}
                                </div>
                            </div>
                            <div
                                class="flex flex-col items-start gap-4 self-stretch"
                            >
                                <p
                                    class="text-slate-500 text-sm md:text-base prose"
                                    v-html="card.rich_text"
                                ></p>
                            </div>
                        </div>
                    </div>

                    <div
                        class="pt-6 flex flex-col sm:flex-row gap-2 items-center justify-start shrink-0 relative"
                    >
                        <div
                            v-if="
                                card.application_link &&
                                card.issuing_bank.link_relationship_type ===
                                    'direct'
                            "
                            class="sm:order-first"
                        >
                            <a
                                :href="card.application_link"
                                class="btn btn-orange !ml-0 !text-md whitespace-nowrap focus:outline-none"
                                v-if="card.issuing_bank.link_button_visible"
                                target="_blank"
                            >
                                Apply Now
                                <i class="fa-regular fa-lock ml-2"></i>
                            </a>
                            <p
                                class="text-center"
                                v-if="card.issuing_bank.link_button_visible"
                            >
                                <small>
                                    on {{ card.issuing_bank.name }}'s secure
                                    site
                                </small>
                            </p>
                        </div>
                        <div
                            v-if="
                                card.application_link &&
                                card.issuing_bank.link_relationship_type ===
                                    'indirect'
                            "
                            class="sm:order-first text-sm font-medium text-white"
                        >
                            <a
                                :href="card.application_link"
                                class="flex p-2.5 px-4 justify-center items-center gap-2 rounded-[var(--radius-md-base,4px)] bg-[var(--Primary-10x-Orange,#FD421D)] shadow-md focus:outline-none focus:ring-2 focus:ring-slate-300 focus:ring-offset-0 active:ring-0"
                                v-if="card.issuing_bank.link_button_visible"
                                target="_blank"
                            >
                                Learn how to apply
                            </a>
                        </div>
                        <div
                            v-if="
                                card.application_link &&
                                card.issuing_bank.link_relationship_type ===
                                    'direct'
                            "
                        >
                            <a
                                :href="card.application_link"
                                class="flex p-2.5 px-4 justify-center items-center gap-2 rounded-[var(--radius-md-base,4px)] bg-[var(--Primary-10x-Orange,#FD421D)] shadow-md focus:outline-none focus:ring-2 focus:ring-slate-300 focus:ring-offset-0 active:ring-0"
                                v-if="card.issuing_bank.link_button_visible"
                                target="_blank"
                            >
                                Apply now
                            </a>
                        </div>
                        <div
                            class="flex flex-col items-start gap-4 self-stretch mt-2 order-first sm:order-none text-slate-500"
                        >
                            <div class="flex items-center gap-2">
                                <icon icon="base-coins" />
                                <div
                                    class="text-text-and-icons-tertiary text-left text-sm relative whitespace-nowrap"
                                >
                                    Annual Fee: ${{ card.annual_fee }}
                                    <span
                                        v-if="
                                            card.issuing_bank &&
                                            (card.issuing_bank.name ===
                                                'American Express' ||
                                                card.issuing_bank.name ===
                                                    'Citi')
                                        "
                                        class="ml-2"
                                    >
                                        <a
                                            :href="card.rates_and_fees_link"
                                            target="_blank"
                                            v-if="card.rates_and_fees_link"
                                            class="text-orange text-xs hover:text-orange-400 focus:outline-none focus:text-orange-600"
                                        >
                                            Rates & Fees
                                        </a>
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div
                    class="inline-flex items-center gap-2 px-2 py-1.5 rounded-t-md bg-[#FD421D] absolute left-10 top-[4.9rem] z-20"
                >
                    <icon icon="sparkles" />
                    <span class="text-white text-xs font-medium">
                        Your top recommended card
                    </span>
                </div>

                <div
                    class="rounded-[50%] w-[124px] h-[124px] absolute right-12 top-24 sm:bottom-24 z-10 hidden sm:block"
                    style="
                        background: linear-gradient(
                            180deg,
                            rgba(230, 241, 249, 1) 0%,
                            rgba(230, 241, 249, 0) 85.50000190734863%
                        );
                    "
                ></div>

                <div
                    class="rounded-[100%] md:w-[30vw] md:h-[30vw] min-w-[450px] min-h-[450px] absolute left-[-2rem] top-[6.7rem] sm:bottom-24 z-10"
                    style="background-color: #e6f1f9"
                ></div>
                <!-- removed: -->
            </div>
        </div>
    </div>
</template>
<script>
import { mapState } from 'pinia';
import { useDashboardStore } from '@/store/dashboard';
import TopCardSkeleton from '@/components/dashboard/cards/top-card-skeleton.vue';
import Icon from '../../ui/icon/icon.vue';

export default {
    props: [],
    components: {
        TopCardSkeleton,
        Icon,
    },
    computed: {
        ...mapState(useDashboardStore, ['loading', 'card']),
        cdnUrl() {
            return window.cdnUrl;
        },
    },
    data: () => ({}),
    methods: {},
    created() {},
    mounted() {},
};
</script>
<style scoped>
:deep(.prose h3) {
    margin-bottom: 0px !important;
    margin-top: 0px !important;
    position: relative;
    padding-left: 24px;
    color: #1e293b;
}

:deep(.prose h3::before) {
    content: '';
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    width: 20px;
    height: 20px;
    background-image: url("data:image/svg+xml,%3Csvg width='20' height='20' viewBox='0 0 20 20' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cg id='points'%3E%3Cpath id='vector' fill-rule='evenodd' clip-rule='evenodd' d='M5.00398 4.58341C5.01804 4.61107 5.05738 4.6698 5.1614 4.75763C5.35252 4.919 5.67847 5.10187 6.15419 5.27177C7.09837 5.60898 8.45753 5.83342 9.9987 5.83342C11.5399 5.83342 12.899 5.60898 13.8432 5.27177C14.3189 5.10187 14.6449 4.919 14.836 4.75763C14.94 4.6698 14.9794 4.61107 14.9934 4.58341C14.9794 4.55576 14.94 4.49703 14.836 4.4092C14.6449 4.24783 14.3189 4.06496 13.8432 3.89506C12.899 3.55785 11.5399 3.33341 9.9987 3.33341C8.45753 3.33341 7.09837 3.55785 6.15419 3.89506C5.67847 4.06496 5.35252 4.24783 5.1614 4.4092C5.05738 4.49703 5.01804 4.55576 5.00398 4.58341ZM14.9987 6.5978C14.8092 6.68678 14.6097 6.76778 14.4038 6.84134C13.2367 7.25815 11.6792 7.50008 9.9987 7.50008C8.31821 7.50008 6.7607 7.25815 5.59363 6.84134C5.38765 6.76778 5.18818 6.68678 4.9987 6.5978V8.11342C5.00625 8.13306 5.03916 8.19608 5.1614 8.2993C5.35252 8.46067 5.67847 8.64354 6.15419 8.81344C7.09837 9.15064 8.45753 9.37508 9.9987 9.37508C11.5399 9.37508 12.899 9.15064 13.8432 8.81344C14.3189 8.64354 14.6449 8.46067 14.836 8.2993C14.9582 8.19608 14.9911 8.13306 14.9987 8.11342V6.5978ZM14.9987 10.1395C14.8092 10.2284 14.6097 10.3094 14.4038 10.383C13.2367 10.7998 11.6792 11.0417 9.9987 11.0417C8.31821 11.0417 6.7607 10.7998 5.59363 10.383C5.38765 10.3094 5.18818 10.2284 4.9987 10.1395V11.6551C5.00625 11.6747 5.03916 11.7377 5.1614 11.841C5.35252 12.0023 5.67847 12.1852 6.15419 12.3551C7.09837 12.6923 8.45753 12.9167 9.9987 12.9167C11.5399 12.9167 12.899 12.6923 13.8432 12.3551C14.3189 12.1852 14.6449 12.0023 14.836 11.841C14.9582 11.7377 14.9912 11.6747 14.9987 11.6551V10.1395ZM14.9987 13.6811C14.8092 13.7701 14.6097 13.8511 14.4038 13.9247C13.2367 14.3415 11.6792 14.5834 9.9987 14.5834C8.31821 14.5834 6.7607 14.3415 5.59363 13.9247C5.38765 13.8511 5.18818 13.7701 4.9987 13.6811V15.4051C5.00625 15.4247 5.03916 15.4877 5.1614 15.591C5.35252 15.7523 5.67847 15.9352 6.15419 16.1051C7.09837 16.4423 8.45753 16.6667 9.9987 16.6667C11.5399 16.6667 12.899 16.4423 13.8432 16.1051C14.3189 15.9352 14.6449 15.7523 14.836 15.591C14.9582 15.4877 14.9911 15.4247 14.9987 15.4051V13.6811ZM16.6654 15.4167C16.6654 16.0448 16.3066 16.5305 15.9112 16.8644C15.512 17.2015 14.9837 17.4676 14.4038 17.6747C13.2367 18.0915 11.6792 18.3334 9.9987 18.3334C8.31821 18.3334 6.7607 18.0915 5.59363 17.6747C5.01372 17.4676 4.4854 17.2015 4.08615 16.8644C3.69076 16.5305 3.33203 16.0448 3.33203 15.4167V4.58341C3.33203 3.95534 3.69076 3.46963 4.08615 3.13578C4.4854 2.79865 5.01372 2.5326 5.59363 2.32549C6.7607 1.90868 8.31821 1.66675 9.9987 1.66675C11.6792 1.66675 13.2367 1.90868 14.4038 2.32549C14.9837 2.5326 15.512 2.79865 15.9112 3.13578C16.3066 3.46963 16.6654 3.95534 16.6654 4.58341V15.4167Z' fill='%231E293B'/%3E%3C/g%3E%3C/svg%3E");
    background-size: contain;
    background-repeat: no-repeat;
}
</style>
